import type { ConfirmCardData } from '../components/VoiceConfirmCard';
import type { VoiceMessage } from './voiceSessionStorage';
import { getPendingContactConfirmation } from './voicePendingConfirmation';
import { getPendingDocumentFlow } from './voicePendingDocumentFlow';

export interface VoiceCommandClientState {
  isOpen: boolean;
  isMinimized: boolean;
  isRecording: boolean;
  messages: VoiceMessage[];
  assistantContext?: Record<string, unknown>;
}

export interface VoiceCommandResult {
  success?: boolean;
  message?: string;
  action?: string;
  phase?: string;
  session_id?: string;
  nextQuestion?: string;
  missingFields?: string[];
  needsConfirmation?: boolean;
  pendingAction?: { data?: Record<string, unknown> };
  data?: any;
  shared_mind?: any;
  suggestions?: unknown[];
  next_steps?: unknown[];
  conversation_state?: {
    original_intent?: string;
    current_intent?: string;
    status?: string;
  };
}

export const ensureVoiceSessionId = (): string => {
  let sessionId = localStorage.getItem('voiceSessionId');
  if (!sessionId) {
    sessionId = `voice_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem('voiceSessionId', sessionId);
  }
  return sessionId;
};

export const buildVoiceCommandRequest = (text: string, sessionId: string, state: VoiceCommandClientState) => {
  const currentPage = window.location.pathname || '/dashboard';
  const currentSearch = window.location.search || '';
  const gvaState: any = (window as any).__GVA_STATE__ || {};
  const pendingContactConfirmation = getPendingContactConfirmation();
  const pendingDocumentFlow = getPendingDocumentFlow();
  const assistantContext = state.assistantContext ?? gvaState.assistantContext;
  const initiatedFrom = assistantContext?.initiatedFrom;
  return {
    voice_text: text,
    session_id: sessionId,
    current_page: `${currentPage}${currentSearch}`,
    page_context: {
      url: `${currentPage}${currentSearch}`,
      pathname: currentPage,
      search: currentSearch,
      title: document.title,
      timestamp: new Date().toISOString(),
      ...(assistantContext ? { assistant_context: assistantContext } : {}),
      ...(initiatedFrom ? { initiated_from: initiatedFrom } : {}),
    },
    client_state: {
      current_page: `${currentPage}${currentSearch}`,
      initiated_from: initiatedFrom || null,
      pending_contact_confirmation: pendingContactConfirmation,
      pending_document_flow: pendingDocumentFlow,
      ui_state: {
        is_open: state.isOpen,
        is_minimized: state.isMinimized,
        is_recording: state.isRecording,
        message_count: state.messages.length
      },
      selected_items: gvaState.selectedItems || [],
      filters: gvaState.filters || {},
      recent_interactions: state.messages.slice(-5).map((message) => ({
        role: message.role,
        content: message.content.substring(0, 100),
        timestamp: message.timestamp
      }))
    }
  };
};

export const requestServerFastNavigation = async (text: string) => {
  const response = await fetch('http://localhost:5001/api/voice/navigate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`
    },
    body: JSON.stringify({ voice_text: text })
  });
  if (!response.ok) return null;
  const result = await response.json();
  return result.success && result.fast_track ? result : null;
};

export const sendVoiceCommand = async (text: string, sessionId: string, state: VoiceCommandClientState): Promise<VoiceCommandResult> => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('Please log in to use voice commands.');

  const response = await fetch('http://localhost:5001/api/voice/command', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(buildVoiceCommandRequest(text, sessionId, state))
  });
  if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  return response.json();
};

export const storeSharedMind = (result: VoiceCommandResult): void => {
  if (!result.shared_mind) return;
  const { unified_context, predicted_action, proactive_suggestions } = result.shared_mind;
  const gvaState: any = (window as any).__GVA_STATE__ || ((window as any).__GVA_STATE__ = {});
  gvaState.sharedMind = {
    unified_context,
    predicted_action,
    proactive_suggestions,
    session_id: result.session_id
  };
};

export const getProactiveMessages = (result: VoiceCommandResult): Array<{ content: string; delay: number }> => {
  const messages: Array<{ content: string; delay: number }> = [];
  const { predicted_action, proactive_suggestions } = result.shared_mind || {};
  if (predicted_action?.confidence >= 0.7 && predicted_action.suggestion) {
    messages.push({ content: predicted_action.suggestion, delay: 2000 });
  }
  const topSuggestion = proactive_suggestions?.[0];
  if (topSuggestion?.priority === 'high' && topSuggestion.text) {
    messages.push({ content: topSuggestion.text, delay: 3000 });
  }
  return messages;
};

export const createVoiceConfirmCard = (
  result: VoiceCommandResult,
  onConfirm: () => void,
  onCancel: () => void
): ConfirmCardData | null => {
  if (!result.needsConfirmation || !result.pendingAction?.data) return null;
  const data = result.pendingAction.data;
  return {
    title: result.message?.includes('invoice') ? 'Confirm Invoice' : 'Confirm Action',
    fields: Object.entries(data)
      .filter(([key]) => !['business_id', 'is_active'].includes(key))
      .slice(0, 6)
      .map(([key, value]) => ({
        label: key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()),
        value: String(value ?? '')
      })),
    onConfirm,
    onCancel
  };
};

export const getVoiceMessageExtras = (result: VoiceCommandResult) => ({
  category:
    result.action === 'navigate'
      ? undefined
      : result.data?.invoice
      ? 'Financial'
      : result.data?.report
      ? 'Reports'
      : undefined,
  card: result.data?.invoice
    ? {
        type: 'invoice' as const,
        title: result.data.invoice.number || 'Invoice Draft',
        subtitle: result.data.invoice.customer,
        amount: result.data.invoice.amount,
        status: 'DRAFT'
      }
    : undefined
});

export const isFeedbackBlocked = (result: VoiceCommandResult): boolean =>
  result.phase === 'feedback_blocked' || result.message === '';

export const isIntentFullyExecuted = (result: VoiceCommandResult): boolean => {
  const message = (result.message || '').trim();
  if (
    message === 'Transaction processed. Please review.'
    || message === 'Done.'
    || result.action === 'clarify'
    || result.phase === 'gathering_quote'
    || result.data?.phase === 'gathering_quote'
  ) {
    return false;
  }

  return (
    result.success === true &&
    !result.phase &&
    !result.needsConfirmation &&
    !!message &&
    ['successfully', 'completed', 'created', 'recorded', 'converted', 'has been'].some((word) =>
      message.toLowerCase().includes(word)
    )
  );
};

export interface VoiceQuickPickOption {
  id: number;
  name: string;
  index: number;
  quote_number?: string;
}

export const extractVoiceQuickPicks = (result: VoiceCommandResult): VoiceQuickPickOption[] => {
  const rawCustomers = result.data?.suggested_customers;
  if (Array.isArray(rawCustomers)) {
    const customers = rawCustomers.filter(
      (option): option is VoiceQuickPickOption =>
        !!option &&
        typeof option.id === 'number' &&
        typeof option.name === 'string' &&
        typeof option.index === 'number'
    );
    if (customers.length > 0) return customers;
  }

  const rawSuppliers = result.data?.suggested_suppliers;
  if (Array.isArray(rawSuppliers)) {
    const suppliers = rawSuppliers.filter(
      (option): option is VoiceQuickPickOption =>
        !!option &&
        typeof option.id === 'number' &&
        typeof option.name === 'string' &&
        typeof option.index === 'number'
    );
    if (suppliers.length > 0) return suppliers;
  }

  const rawQuotes = result.data?.suggested_quotes;
  if (!Array.isArray(rawQuotes)) return [];
  return rawQuotes.filter(
    (option): option is VoiceQuickPickOption =>
      !!option &&
      typeof option.id === 'number' &&
      typeof option.name === 'string' &&
      typeof option.index === 'number'
  ).map((option) => ({
    ...option,
    quote_number: typeof option.quote_number === 'string' ? option.quote_number : undefined,
  }));
};

export const shouldCloseVoiceAssistant = (result: VoiceCommandResult): boolean =>
  !!result.data?.quote_id ||
  !!result.data?.invoice_id ||
  (!!result.data?.quote_number &&
    (result.message?.toLowerCase().includes('has been created successfully') ?? false)) ||
  (!!result.data?.invoice_number &&
    (result.message?.toLowerCase().includes('has been created') ?? false));

export const needsContinuation = (result: VoiceCommandResult): boolean =>
  result.phase === 'gathering_data' ||
  result.phase === 'gathering_customer' ||
  result.phase === 'gathering_quote' ||
  result.phase === 'gathering_items' ||
  result.phase === 'confirmation' ||
  result.phase === 'contact_confirmation' ||
  result.data?.phase === 'gathering_quote' ||
  result.needsConfirmation === true ||
  !!(
    result.message &&
    (result.message.includes('?') ||
      result.message.toLowerCase().includes('what') ||
      result.message.toLowerCase().includes('please') ||
      result.message.toLowerCase().includes('need') ||
      result.message.toLowerCase().includes('required'))
  );
